import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic card for API key / credential integrations.
 * 
 * @param {string} title - Card title
 * @param {string} description - Card description
 * @param {React.Component} icon - Lucide icon component
 * @param {string} iconColor - Tailwind text color class for icon
 * @param {Array} fields - Array of { key, label, placeholder, secret, hint }
 * @param {Function} onSave - async (data, token) => void. Receives field values + auth token.
 * @param {string} token - Auth token
 * @param {string} saveLabel - Button text (default: "Sauvegarder")
 */
export function ApiKeyCard({
  title,
  description,
  icon: Icon,
  iconColor = 'text-muted-foreground',
  fields,
  onSave,
  token,
  saveLabel = 'Sauvegarder',
  testIdPrefix,
}) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, '']))
  );
  const [visible, setVisible] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, false]))
  );
  const [loading, setLoading] = useState(false);

  const allFilled = fields.every((f) => values[f.key]?.trim());

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(values, token);
      setValues(Object.fromEntries(fields.map((f) => [f.key, ''])));
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erreur lors de la sauvegarde ${title}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid={`${testIdPrefix}-card`}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <CardTitle className="font-heading">{title}</CardTitle>
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((field) => (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={`${testIdPrefix}-${field.key}`}>{field.label}</Label>
            <div className="relative">
              <Input
                id={`${testIdPrefix}-${field.key}`}
                type={field.secret && !visible[field.key] ? 'password' : 'text'}
                placeholder={field.placeholder}
                value={values[field.key]}
                onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                className={field.secret ? 'pr-10' : ''}
                data-testid={`${testIdPrefix}-${field.key}-input`}
              />
              {field.secret && (
                <button
                  type="button"
                  onClick={() => setVisible({ ...visible, [field.key]: !visible[field.key] })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {visible[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
            </div>
            {field.hint && <p className="text-xs text-muted-foreground">{field.hint}</p>}
          </div>
        ))}
        <Button
          onClick={handleSave}
          disabled={loading || !allFilled}
          data-testid={`${testIdPrefix}-save-btn`}
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Validation...</> : saveLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
